/**
 * Блок «Talanto»: ключ MCP пользователя, импорт откликов и сверка статусов (каждые 15 минут).
 */
import { fmtDate } from '@jt/core';
import type { IntegrationsStatus } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, del, post, put } from '../api/client';
import { keys, refreshSummaries } from '../api/hooks';
import { ConfirmButton, useUI } from '../components/overlays';
import { Icon } from '../components/ui';
import { integrationsKey, useIntegrations } from '../pages/integrations';

const when = (iso: string | null) => (iso ? `${fmtDate(iso.slice(0, 10))}, ${iso.slice(11, 16)} UTC` : '');
const ext = { target: '_blank', rel: 'noopener noreferrer' } as const;

function KeyGuide() {
  return (
    <>
      <b>Где взять ключ Talanto</b>
      <ol>
        <li>
          Войдите на{' '}
          <a href="https://talanto.work/" {...ext}>
            talanto.work
          </a>
          . MCP есть на тарифах <b>Boost</b> и <b>Sampler</b> (
          <a href="https://talanto.work/plans" {...ext}>
            тарифы
          </a>
          ).
        </li>
        <li>
          В личном кабинете откройте раздел про MCP / подключение AI-агентов: там <b>Endpoint</b>{' '}
          <code>https://talanto.work/mcp/</code> и ключ вида <code>talanto_…</code>.
        </li>
        <li>Скопируйте ключ целиком и вставьте сюда — трекер проверит его и сохранит зашифрованным.</li>
      </ol>
      <p>
        Ключ у каждого пользователя свой. Если он куда-то утёк — выпустите в кабинете новый и замените здесь.
      </p>
    </>
  );
}

export function TalantoSettings() {
  const ui = useUI();
  const qc = useQueryClient();
  const q = useIntegrations();
  const t = q.data?.talanto;
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState(false);
  if (!t) return null;
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: integrationsKey });
    void qc.invalidateQueries({ queryKey: keys.apps() });
    refreshSummaries(qc);
  };
  const editing = !t.connected || t.needsReauth;
  return (
    <>
      <p className="muted sm" style={{ marginTop: 0 }}>
        Отклики из Talanto сами появляются в «Моих откликах» (без дублей), а статусы сверяются в обе стороны:
        сменили статус здесь — он меняется в Talanto, и наоборот. Проверка — каждые 15 минут.
      </p>
      {t.connected && (
        <div className="mailacc">
          <p className="sm" style={{ margin: '0 0 8px' }}>
            Подключено{t.tier ? ` · тариф ${t.tier}` : ''}
            {t.periodEnd ? ` до ${fmtDate(t.periodEnd.slice(0, 10))}` : ''} · связано откликов: {t.linked}
            {t.needsReauth && (
              <span style={{ color: 'var(--danger)' }}> — Talanto не принял ключ или закончился тариф</span>
            )}
          </p>
          <div className="btnrow">
            <button
              className="btn primary"
              type="button"
              disabled={busy || t.needsReauth}
              onClick={async () => {
                setBusy(true);
                try {
                  await post('/integrations/talanto/sync');
                  ui.toast('Синхронизирую с Talanto — новые отклики появятся в «Моих откликах»');
                  setTimeout(() => void refresh(), 6000);
                } catch (x) {
                  ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Icon name="restore" /> Синхронизировать сейчас
            </button>
            <span className="muted sm" style={{ alignSelf: 'center' }}>
              {t.lastSyncAt ? `синхронизировано ${when(t.lastSyncAt)}` : 'ещё не синхронизировалось'}
              {t.lastError ? ` · ошибка: ${t.lastError}` : ''}
            </span>
            <ConfirmButton
              className="btn sm ghost danger"
              confirmText="Отключить Talanto?"
              onConfirm={async () => {
                await del('/integrations/talanto');
                await refresh();
                ui.toast('Talanto отключён; импортированные вакансии остались');
              }}
            >
              Отключить
            </ConfirmButton>
          </div>
        </div>
      )}
      {editing && (
        <form
          className="secret"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setErr('');
            try {
              qc.setQueryData(
                integrationsKey,
                await put<IntegrationsStatus>('/integrations/talanto', { key }),
              );
              setKey('');
              ui.toast('Talanto подключён — импортирую отклики');
              setTimeout(() => void refresh(), 6000);
            } catch (x) {
              setErr(x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Не получилось');
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="secret-h">
            <span className="secret-l">
              <label htmlFor="talanto-key">Ключ MCP Talanto</label>
              <button
                type="button"
                className="info-btn"
                aria-label="Как получить: Ключ MCP Talanto"
                aria-expanded={info}
                aria-controls="talanto-key-info"
                onClick={() => setInfo(!info)}
              >
                i
              </button>
            </span>
            <span className="sm muted">{t.connected ? 'нужен новый ключ' : 'не задан'}</span>
          </div>
          {info && (
            <div className="info-pop" id="talanto-key-info" role="note">
              <KeyGuide />
            </div>
          )}
          <div className="secret-row">
            <input
              id="talanto-key"
              type="password"
              autoComplete="new-password"
              spellCheck={false}
              value={key}
              placeholder="talanto_…"
              aria-invalid={err ? true : undefined}
              onChange={(e) => setKey(e.target.value)}
            />
            <button className="btn primary" type="submit" disabled={busy || !key.trim()}>
              {busy ? 'Проверяю…' : 'Проверить и подключить'}
            </button>
          </div>
          {err && (
            <p className="sm" role="alert" style={{ color: 'var(--danger)', margin: '6px 0 0' }}>
              {err}
            </p>
          )}
        </form>
      )}
    </>
  );
}
